import { GridContainer, GridItem, GridItemOwnProps } from '@entur/grid';
import { Children, ReactElement, ReactNode, cloneElement, useEffect, useRef, useState } from 'react';
import Actions from './Actions';
import styles from './styles.module.scss';
import { isReactElementWithProps } from 'components/types';

interface SearchBoxProps {
  children: ReactNode;
  tabs?: boolean;
}
const columns = 12;
const maxInputFields = [2, 3, 4, 6];
const largeWidth = 1600;
const extraLargeWidth = 1920;

const SearchBox = ({ children, tabs = false }: SearchBoxProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const childrenArray = Children.toArray(children);
  const inputs = childrenArray.filter((child) => (child as ReactElement).type !== Actions);
  const actions = childrenArray.find((child) => (child as ReactElement).type === Actions);

  const initialSmall = (columns / Math.min(inputs.length, maxInputFields[0])) as GridItemOwnProps['small'];
  const initialMedium = (columns / Math.min(inputs.length, maxInputFields[1])) as GridItemOwnProps['medium'];
  const initialLarge = (columns / Math.min(inputs.length, maxInputFields[2])) as GridItemOwnProps['large'];

  const [large, setLarge] = useState<GridItemOwnProps['large']>(initialLarge);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;

      if (inputs.length > 4 && screenWidth > extraLargeWidth) {
        setLarge((columns / maxInputFields[3]) as GridItemOwnProps['large']);
      } else {
        setLarge((columns / Math.min(inputs.length, maxInputFields[2])) as GridItemOwnProps['large']);
      }

      const breakLine =
        (screenWidth < largeWidth && inputs.length > 2) || (inputs.length > 4 && screenWidth < extraLargeWidth);
      const lineClass = breakLine ? styles.multiLines : styles.oneLine;
      ref.current?.classList.remove(styles.oneLine, styles.multiLines);
      ref.current?.classList.add(lineClass);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [inputs.length]);

  return (
    <div className={!tabs ? styles.searchBox : ''} ref={ref}>
      <GridContainer className={styles.gridContainer}>
        {inputs.map((child, index) => {
          if (isReactElementWithProps(child)) {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <GridItem key={index} small={initialSmall} medium={initialMedium} large={large}>
                {cloneElement(child, {
                  ...child.props,
                  className: child.props.className,
                })}
              </GridItem>
            );
          }
        })}
      </GridContainer>
      {actions && actions}
    </div>
  );
};

SearchBox.Actions = Actions;

export default SearchBox;
